import SummaryReportsAPI from 'dashboard/api/summaryReports';
import camelcaseKeys from 'camelcase-keys';

const typeMap = {
  inbox: {
    flagKey: 'isFetchingInboxSummaryReports',
    apiMethod: 'getInboxReports',
    mutationKey: 'setInboxSummaryReport',
  },
  agent: {
    flagKey: 'isFetchingAgentSummaryReports',
    apiMethod: 'getAgentReports',
    mutationKey: 'setAgentSummaryReport',
  },
  team: {
    flagKey: 'isFetchingTeamSummaryReports',
    apiMethod: 'getTeamReports',
    mutationKey: 'setTeamSummaryReport',
  },
  vip: {
    flagKey: 'isFetchingVipSummaryReports',
    apiMethod: 'getVipReports',
    mutationKey: 'setVipSummaryReport',
  },
  vip_agent: {
    flagKey: 'isFetchingVipAgentSummaryReports',
    apiMethod: 'getVipAgentReports',
    mutationKey: 'setVipAgentSummaryReport',
  },
  issue_agent: {
    flagKey: 'isFetchingIssueAgentSummaryReports',
    apiMethod: 'getIssueAgentReports',
    mutationKey: 'setIssueAgentSummaryReport',
  },
  issue_type: {
    flagKey: 'isFetchingIssueTypeSummaryReports',
    apiMethod: 'getIssueTypeReports',
    mutationKey: 'setIssueTypeSummaryReport',
  },
  label: {
    flagKey: 'isFetchingLabelSummaryReports',
    apiMethod: 'getLabelReports',
    mutationKey: 'setLabelSummaryReport',
  },
};

async function fetchSummaryReports(type, params, { commit }) {
  const config = typeMap[type];
  if (!config) return false;

  try {
    commit('setUIFlags', { [config.flagKey]: true });
    const response = await SummaryReportsAPI[config.apiMethod](params);
    commit(config.mutationKey, camelcaseKeys(response.data, { deep: true }));
    return true;
  } catch (error) {
    // Ignore error
    return false;
  } finally {
    commit('setUIFlags', { [config.flagKey]: false });
  }
}

export const initialState = {
  inboxSummaryReports: [],
  agentSummaryReports: [],
  teamSummaryReports: [],
  vipSummaryReports: [],
  vipAgentSummaryReports: [],
  issueAgentSummaryReports: [],
  issueTypeSummaryReports: [],
  labelSummaryReports: [],
  // Agent sessions for drill-down
  agentSessions: {
    sessions: [],
    pagination: {
      page: 1,
      perPage: 50,
      totalCount: 0,
      totalPages: 0,
    },
  },
  uiFlags: {
    isFetchingInboxSummaryReports: false,
    isFetchingAgentSummaryReports: false,
    isFetchingTeamSummaryReports: false,
    isFetchingVipSummaryReports: false,
    isFetchingVipAgentSummaryReports: false,
    isFetchingIssueAgentSummaryReports: false,
    isFetchingIssueTypeSummaryReports: false,
    isFetchingLabelSummaryReports: false,
    isFetchingAgentSessions: false,
  },
};

export const getters = {
  getInboxSummaryReports(state) {
    return state.inboxSummaryReports;
  },
  getAgentSummaryReports(state) {
    return state.agentSummaryReports;
  },
  getTeamSummaryReports(state) {
    return state.teamSummaryReports;
  },
  getVipSummaryReports(state) {
    return state.vipSummaryReports;
  },
  getVipAgentSummaryReports(state) {
    return state.vipAgentSummaryReports;
  },
  getIssueAgentSummaryReports(state) {
    return state.issueAgentSummaryReports;
  },
  getIssueTypeSummaryReports(state) {
    return state.issueTypeSummaryReports;
  },
  getLabelSummaryReports(state) {
    return state.labelSummaryReports;
  },
  getAgentSessions(state) {
    return state.agentSessions;
  },
  getUIFlags(state) {
    return state.uiFlags;
  },
};

export const actions = {
  fetchInboxSummaryReports({ commit }, params) {
    return fetchSummaryReports('inbox', params, { commit });
  },

  fetchAgentSummaryReports({ commit }, params) {
    return fetchSummaryReports('agent', params, { commit });
  },

  fetchTeamSummaryReports({ commit }, params) {
    return fetchSummaryReports('team', params, { commit });
  },

  fetchVipSummaryReports({ commit }, params) {
    return fetchSummaryReports('vip', params, { commit });
  },

  fetchVipAgentSummaryReports({ commit }, params) {
    return fetchSummaryReports('vip_agent', params, { commit });
  },

  fetchLabelSummaryReports({ commit }, params) {
    return fetchSummaryReports('label', params, { commit });
  },

  fetchIssueAgentSummaryReports({ commit }, params) {
    return fetchSummaryReports('issue_agent', params, { commit });
  },

  fetchIssueTypeSummaryReports({ commit }, params) {
    return fetchSummaryReports('issue_type', params, { commit });
  },

  // Fetch individual sessions for an agent (for QA drill-down)
  async fetchAgentSessions({ commit }, params) {
    try {
      commit('setUIFlags', { isFetchingAgentSessions: true });
      const response = await SummaryReportsAPI.getAgentSessions(params);
      commit('setAgentSessions', camelcaseKeys(response.data, { deep: true }));
      return true;
    } catch (error) {
      // Handle error silently or show notification
      commit('setAgentSessions', {
        sessions: [],
        pagination: { page: 1, perPage: 50, totalCount: 0, totalPages: 0 },
      });
      return false;
    } finally {
      commit('setUIFlags', { isFetchingAgentSessions: false });
    }
  },

  clearAgentSessions({ commit }) {
    commit('setAgentSessions', {
      sessions: [],
      pagination: { page: 1, perPage: 50, totalCount: 0, totalPages: 0 },
    });
  },
};

export const mutations = {
  setInboxSummaryReport(state, data) {
    state.inboxSummaryReports = data;
  },
  setAgentSummaryReport(state, data) {
    state.agentSummaryReports = data;
  },
  setTeamSummaryReport(state, data) {
    state.teamSummaryReports = data;
  },
  setVipSummaryReport(state, data) {
    state.vipSummaryReports = data;
  },
  setVipAgentSummaryReport(state, data) {
    state.vipAgentSummaryReports = data;
  },
  setIssueAgentSummaryReport(state, data) {
    state.issueAgentSummaryReports = data;
  },
  setIssueTypeSummaryReport(state, data) {
    state.issueTypeSummaryReports = data;
  },
  setLabelSummaryReport(state, data) {
    state.labelSummaryReports = data;
  },
  setAgentSessions(state, data) {
    state.agentSessions = data;
  },
  setUIFlags(state, uiFlag) {
    state.uiFlags = { ...state.uiFlags, ...uiFlag };
  },
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  actions,
  mutations,
};
